// ════════════════════════════════════════════════════════════════════════════
//  ChatDeleteSystem.jsx  ·  NSL Chat Hub V2 — Sistema de Borrado con Partículas
//
//  Exporta:
//    <ParticleExplosion />      — burst de partículas animadas
//    <DeletedTombstone />       — placeholder "mensaje eliminado"
//    <DeleteConvModal />        — modal de confirmación para borrar conv
//    useMessageDelete()         — lógica de borrado con animación
//    <DeletableMessageBubble /> — wrapper de burbuja con botón de borrar
//
//  CSS: ChatDeleteSystem.css   — animaciones y estilos
//
//  FIX P-11 ✅: ParticleExplosion ya no suprime exhaustive-deps.
//    - `particles` es memoizado con useMemo([count]) → estable durante la vida
//      del componente.
//    - `onDone` se captura en onDoneRef para evitar closures rancios sin
//      necesidad de incluirlo en las deps del efecto de timeout.
//    - useLayoutEffect sincroniza el ref ANTES del siguiente paint → seguro.
// ════════════════════════════════════════════════════════════════════════════

import React, {
    useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo, useId,
} from "react";
import "./ChatDeleteSystem.css";

// ─────────────────────────────────────────────────────────────────────────────
//  PARTICLE COLORS (cyberpunk palette — NSL brand)
// ─────────────────────────────────────────────────────────────────────────────

const PARTICLE_COLORS = [
    "#00ffff", "#00ff88", "#ff5e7e", "#f5a623",
    "#c084fc", "#60a5fa", "#f472b6", "#34d399",
    "#ffffff", "#FFE234",
];

const rnd = (min, max) => Math.random() * (max - min) + min;

// ─────────────────────────────────────────────────────────────────────────────
//  PARTICLE EXPLOSION
//
//  FIX P-11: eliminado eslint-disable-line en el useEffect de timeout.
//    Estrategia de dos refs:
//      1. `onDoneRef`   — se sincroniza en useLayoutEffect para que siempre
//                         apunte al `onDone` más reciente sin ser dep del efecto
//                         del timeout (el efecto solo se monta una vez).
//      2. useMemo para `particles` — con dep [count], que es constante durante
//                         la vida del componente. Garantiza que el cálculo de
//                         maxDur no cambie entre renders.
//
//  origin: { x, y }  — coordenadas de pantalla del mensaje
// ─────────────────────────────────────────────────────────────────────────────

export function ParticleExplosion({ origin, onDone, count = 18 }) {
    // ── Ref para onDone: siempre fresco, nunca causa re-fires del efecto ──────
    const onDoneRef = useRef(onDone);
    useLayoutEffect(() => { onDoneRef.current = onDone; }); // sin deps → siempre actualizado

    // ── Partículas memoizadas: generadas una sola vez por instancia ───────────
    const particles = useMemo(() =>
        Array.from({ length: count }, (_, i) => {
            const angle = (i / count) * 360 + rnd(-15, 15);
            const dist = rnd(60, 160);
            const size = rnd(4, 10);
            const dur = rnd(0.45, 0.85);
            const color = PARTICLE_COLORS[i % PARTICLE_COLORS.length];
            const shape = i % 3 === 0 ? "circle" : i % 3 === 1 ? "square" : "star";
            const rad = (angle * Math.PI) / 180;
            return { i, size, dur, color, shape, tx: Math.cos(rad) * dist, ty: Math.sin(rad) * dist };
        }),
        [count]); // count no cambia durante la vida del componente

    // ── Efecto de timeout: dep es `particles` (estable). onDone vía ref. ──────
    useEffect(() => {
        const maxDur = Math.max(...particles.map(p => p.dur)) * 1000 + 100;
        const t = setTimeout(() => onDoneRef.current(), maxDur);
        return () => clearTimeout(t);
    }, [particles]); // particles estable → efecto se ejecuta solo al montar

    const style = {
        position: "fixed",
        left: origin.x,
        top: origin.y,
        width: 0,
        height: 0,
        pointerEvents: "none",
        zIndex: 99999,
    };

    return (
        <div style={style}>
            {particles.map(p => (
                <div
                    key={p.i}
                    className={`nsl-particle nsl-particle--${p.shape}`}
                    style={{
                        "--tx": `${p.tx}px`,
                        "--ty": `${p.ty}px`,
                        "--dur": `${p.dur}s`,
                        "--color": p.color,
                        "--size": `${p.size}px`,
                        width: p.size,
                        height: p.size,
                        background: p.color,
                        animationDuration: `${p.dur}s`,
                    }}
                />
            ))}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  DELETED TOMBSTONE
//  Reemplaza el mensaje borrado en el hilo. Desaparece solo después de ~3.6s.
// ─────────────────────────────────────────────────────────────────────────────

export function DeletedTombstone({ isAdmin = true }) {
    const [fading, setFading] = useState(false);
    const [gone, setGone] = useState(false);

    useEffect(() => {
        const t1 = setTimeout(() => setFading(true), 3000);
        const t2 = setTimeout(() => setGone(true), 3600);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, []);

    if (gone) return null;

    return (
        <div className={`nsl-tombstone ${isAdmin ? "--admin" : "--student"} ${fading ? "--fade" : ""}`}>
            <span className="nsl-tombstone__icon">💬</span>
            <span className="nsl-tombstone__text">Mensaje eliminado</span>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  DELETE CONVERSATION MODAL
//  Solicita al admin escribir "BORRAR" para confirmar.
// ─────────────────────────────────────────────────────────────────────────────

export function DeleteConvModal({ conv, onConfirm, onCancel, loading }) {
    const [typed, setTyped] = useState("");
    const [shaking, setShaking] = useState(false);
    const [launched, setLaunched] = useState(false);

    const confirmed = typed.trim().toUpperCase() === "BORRAR";

    const handleConfirm = () => {
        if (!confirmed) {
            setShaking(true);
            setTimeout(() => setShaking(false), 500);
            return;
        }
        setLaunched(true);
        onConfirm();
    };

    // Prevenir scroll detrás del modal
    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = ""; };
    }, []);

    return (
        <div className="nsl-modal-overlay" onClick={onCancel}>
            <div
                className={`nsl-modal-box ${shaking ? "--shake" : ""} ${launched ? "--launched" : ""}`}
                onClick={e => e.stopPropagation()}
            >
                {/* Destructo icon */}
                <div className="nsl-modal-bomb">
                    <span className="nsl-modal-bomb__ring" />
                    <span className="nsl-modal-bomb__ring --r2" />
                    ☢️
                </div>

                <h3 className="nsl-modal-title">Borrar conversación</h3>

                <p className="nsl-modal-desc">
                    Esta acción es <strong>permanente e irreversible</strong>.
                    Se eliminarán todos los mensajes de{" "}
                    <span style={{ color: conv?.student_color || "var(--er)" }}>
                        {conv?.student_name || "este alumno"}
                    </span>.
                </p>

                <div className="nsl-modal-stats">
                    <span>🗑️ Conversación ID #{conv?.id}</span>
                    <span>📨 {conv?.total_msgs ?? "todos los"} mensajes</span>
                </div>

                <div className="nsl-modal-input-wrap">
                    <label className="nsl-modal-label">
                        Escribe <strong>BORRAR</strong> para confirmar
                    </label>
                    <input
                        className={`nsl-modal-input ${confirmed ? "--ok" : typed ? "--er" : ""}`}
                        value={typed}
                        onChange={e => setTyped(e.target.value)}
                        placeholder="BORRAR"
                        autoFocus
                        spellCheck={false}
                        onKeyDown={e => e.key === "Enter" && handleConfirm()}
                    />
                    {confirmed && <span className="nsl-modal-ok-check">✓</span>}
                </div>

                <div className="nsl-modal-actions">
                    <button className="nsl-modal-btn --cancel" onClick={onCancel} disabled={loading}>
                        Cancelar
                    </button>
                    <button
                        className={`nsl-modal-btn --destroy ${confirmed ? "--armed" : ""}`}
                        onClick={handleConfirm}
                        disabled={loading}
                    >
                        {loading ? "⏳ Borrando…" : "☢️ Confirmar borrado"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  useMessageDelete (BLINDADO - GRADO PRODUCCIÓN)
//  Espera el 200 OK del servidor ANTES de detonar las partículas.
// ─────────────────────────────────────────────────────────────────────────────

export function useMessageDelete(deleteFn) {
    const [phases, setPhases] = useState({});
    const [origins, setOrigins] = useState({});

    const triggerDelete = useCallback(async (msgId, msgRef) => {
        if (!msgRef?.current) return;

        // 1. Bloqueo visual inmediato (anti-doble-click)
        setPhases(prev => ({ ...prev, [msgId]: "pending_db" }));

        try {
            // 2. Llamada al servidor — PRIMERO
            await deleteFn(msgId);

            // 3. Solo si el backend confirma, disparamos la animación
            const rect = msgRef.current.getBoundingClientRect();
            const origin = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };

            setOrigins(prev => ({ ...prev, [msgId]: origin }));
            setPhases(prev => ({ ...prev, [msgId]: "exploding" }));

        } catch (error) {
            // 4. Fallo: revertir estado y avisar al admin
            console.error("Fallo al borrar en BD:", error);
            setPhases(prev => ({ ...prev, [msgId]: "idle" }));
            alert("Error de red: No se pudo borrar el mensaje del servidor. El alumno aún puede verlo.");
        }
    }, [deleteFn]);

    const onParticleDone = useCallback((msgId) => {
        setPhases(prev => ({ ...prev, [msgId]: "tombstone" }));
    }, []);

    const getPhase = useCallback((msgId) => phases[msgId] || "idle", [phases]);
    const getOrigin = useCallback((msgId) => origins[msgId] || null, [origins]);

    return { triggerDelete, onParticleDone, getPhase, getOrigin };
}

// ─────────────────────────────────────────────────────────────────────────────
//  DeletableMessageBubble — wrapper de burbuja con botón de borrar
// ─────────────────────────────────────────────────────────────────────────────

export function DeletableMessageBubble({ msg, onDelete, getPhase, getOrigin, onParticleDone, children }) {
    const msgRef = useRef(null);
    const isAdmin = msg.sender_role === "admin";
    const phase = getPhase(msg.id);
    const origin = getOrigin(msg.id);

    if (phase === "tombstone") {
        return <DeletedTombstone isAdmin={isAdmin} />;
    }

    return (
        <>
            {phase === "exploding" && origin && (
                <ParticleExplosion origin={origin} onDone={() => onParticleDone(msg.id)} />
            )}
            <div
                className={`nsl-msg-wrap ${isAdmin ? "--admin" : ""} ${phase === "exploding" ? "nsl-msg-deleting" : ""}`}
                style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flexDirection: isAdmin ? "row-reverse" : "row",
                }}
            >
                {/* Botón de basura con protección de doble click */}
                {(phase === "idle" || phase === "pending_db") && (
                    <button
                        className="nsl-delete-btn"
                        style={{
                            position: "absolute",
                            top: "0px",
                            [isAdmin ? "right" : "left"]: "0px",
                            transform: "none",
                            zIndex: 50,
                            opacity: phase === "pending_db" ? 0.5 : 1,
                            cursor: phase === "pending_db" ? "wait" : "pointer",
                        }}
                        onClick={() => onDelete(msg.id, msgRef)}
                        disabled={phase === "pending_db"}
                        title="Borrar mensaje"
                    >
                        {phase === "pending_db" ? "⏳" : "🗑"}
                    </button>
                )}

                {/* Contenido del mensaje original */}
                <div ref={msgRef} style={{ opacity: phase === "pending_db" ? 0.7 : 1 }}>
                    {children}
                </div>
            </div>
        </>
    );
}